import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const CART_COUNT_REQUEST_TIMEOUT = 10000;
const CART_COUNT_RETRY_DELAY = 1500;

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #cartUpdateRevision = 0;

  /** @type {number} */
  #lastAppliedCartUpdateRevision = 0;

  /** @type {Set<number>} */
  #pendingCartUpdateRevisions = new Set();

  /** @type {Promise<void> | null} */
  #cartCountRefresh = null;

  /** @type {boolean} */
  #needsCartCountReconciliation = false;

  /** @type {number | undefined} */
  #cartCountRetryTimer;

  /** @type {number} */
  #lastRetriedCartUpdateRevision = -1;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    if (CartIcon.#getNavigationType() === 'back_forward') this.#reconcileHistoryCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#cartCountRetryTimer !== undefined) {
      clearTimeout(this.#cartCountRetryTimer);
      this.#cartCountRetryTimer = undefined;
    }
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) this.#reconcileHistoryCart();
  };

  #reconcileHistoryCart() {
    this.#needsCartCountReconciliation = true;
    if (this.#pendingCartUpdateRevisions.size === 0) void this.#reconcileCartCount();
  }

  static #getNavigationType() {
    const entry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    return entry?.entryType === 'navigation' && 'type' in entry ? entry.type : undefined;
  }

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const revision = ++this.#cartUpdateRevision;
    if (this.#cartCountRetryTimer !== undefined) {
      clearTimeout(this.#cartCountRetryTimer);
      this.#cartCountRetryTimer = undefined;
    }
    const eventPromise = event.promise;

    if (!eventPromise || typeof eventPromise.then !== 'function') {
      this.#needsCartCountReconciliation = true;
      if (this.#pendingCartUpdateRevisions.size === 0) void this.#reconcileCartCount();
      return;
    }

    this.#pendingCartUpdateRevisions.add(revision);

    Promise.resolve(eventPromise)
      .then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount;

        if (revision === this.#cartUpdateRevision && Number.isFinite(itemCount)) {
          this.#applyCartCount(Number(itemCount), revision);
        } else {
          this.#needsCartCountReconciliation = true;
        }
      })
      .catch((error) => {
        this.#needsCartCountReconciliation = true;
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      })
      .finally(() => {
        this.#pendingCartUpdateRevisions.delete(revision);
        if (this.#pendingCartUpdateRevisions.size === 0 && this.#needsCartCountReconciliation) {
          void this.#reconcileCartCount();
        }
      });
  };

  /**
   * @param {number} itemCount
   * @param {number} revision
   */
  #applyCartCount(itemCount, revision) {
    if (!this.isConnected) return;
    this.#lastAppliedCartUpdateRevision = Math.max(this.#lastAppliedCartUpdateRevision, revision);
    void this.renderCartBubble(itemCount);
    const liveRegion = document.querySelector('[data-testid="cart-count-live-region"]');
    const message = `${Theme.translations.cart_count}: ${itemCount}`;
    if (liveRegion && liveRegion.textContent !== message) liveRegion.textContent = message;
  }

  async #reconcileCartCount() {
    if (this.#cartCountRefresh) return this.#cartCountRefresh;

    this.#needsCartCountReconciliation = false;
    const revision = this.#cartUpdateRevision;
    const refresh = this.#fetchCurrentCartCount()
      .then((itemCount) => {
        if (!this.isConnected) return;
        if (this.#pendingCartUpdateRevisions.size === 0 && revision === this.#cartUpdateRevision) {
          this.#applyCartCount(itemCount, revision);
        } else if (this.#lastAppliedCartUpdateRevision < this.#cartUpdateRevision) {
          this.#needsCartCountReconciliation = true;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Could not reconcile cart count:', error);
        if (
          this.isConnected &&
          revision === this.#cartUpdateRevision &&
          this.#lastRetriedCartUpdateRevision !== revision
        ) {
          this.#lastRetriedCartUpdateRevision = revision;
          this.#cartCountRetryTimer = setTimeout(() => {
            this.#cartCountRetryTimer = undefined;
            if (
              !this.isConnected ||
              revision !== this.#cartUpdateRevision ||
              this.#pendingCartUpdateRevisions.size > 0
            ) {
              return;
            }
            this.#needsCartCountReconciliation = true;
            void this.#reconcileCartCount();
          }, CART_COUNT_RETRY_DELAY);
        }
      })
      .finally(() => {
        if (this.#cartCountRefresh === refresh) this.#cartCountRefresh = null;
        if (
          this.isConnected &&
          this.#pendingCartUpdateRevisions.size === 0 &&
          this.#needsCartCountReconciliation
        ) {
          void this.#reconcileCartCount();
        }
      });

    this.#cartCountRefresh = refresh;
    return refresh;
  }

  async #fetchCurrentCartCount() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (
      cartItemsComponent &&
      'fetchCartData' in cartItemsComponent &&
      typeof cartItemsComponent.fetchCartData === 'function'
    ) {
      const cart = await cartItemsComponent.fetchCartData();
      if (!Number.isFinite(cart?.item_count)) throw new Error('Cart response was missing its item count');
      return Number(cart.item_count);
    }

    const abortController = new AbortController();
    const timeout = setTimeout(() => abortController.abort(), CART_COUNT_REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });
      if (!response.ok) throw new Error(`Failed to fetch cart count: ${response.status} ${response.statusText}`);

      const cart = await response.json();
      if (!Number.isFinite(cart?.item_count)) throw new Error('Cart response was missing its item count');
      return Number(cart.item_count);
    } finally {
      clearTimeout(timeout);
    }
  }

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // Storage can be unavailable in privacy modes; the rendered count remains authoritative.
    }

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    let sessionStorageCount = null;
    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (_) {
      return;
    }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
